import { $Keys, $Values } from "utility-types";
import { RecursivePartial } from "./interfaces/utilities";

/**
 * Function to make a field that is undefined/null in object to string
 * example use case
 * - mostly related to edit input. the <input> component value type should be a string
 *   meanwhile data returned from backend for empty value could be null or the field
 *   could be non existent
 */
export const stringifyObjectFields = <T extends Record<string, unknown>>(
  data: T,
): T => {
  return Object.keys(data).reduce((prev, next) => {
    (prev as Record<string, unknown>)[next] = data[next] ?? "";
    return prev;
  }, {}) as T;
};

/**
 * check if object or value is non nullish
 * return true if object non nullish,
 * otherwise, false
 * @param value
 * @returns
 */
export const isNonNullish = <T>(value: T): boolean =>
  typeof value !== "undefined" && value !== null;

/**
 * Function to filter object with a function
 * example is for removing nullish value
 *
 * @example
 * filterObjectFiledsKey(obj, isNonNullish)
 * @param obj
 * @param filterFn
 * @returns
 */
export const filterObjectFieldsKey = <T extends Record<string, unknown>>(
  obj: T,
  filterFn: (value: $Values<T>, key: $Keys<T>) => boolean,
) => {
  return Object.keys(obj).reduce(
    (prev, next) => {
      const key = next as $Keys<T>;
      if (filterFn(obj[key], key)) {
        prev[key] = obj[key];
      }
      return prev;
    },
    {} as Partial<T>,
  );
};

/**
 * Maps over the top-level properties of an object.
 * @param obj - The source object
 * @param fn - Mapping function for each value
 * @returns A new object with mapped values
 */
export function mapObject<T extends object, R>(
  obj: T,
  fn: <K extends keyof T>(value: T[K], key: K, obj: T) => R,
): { [K in keyof T]: R } {
  const result = {} as { [K in keyof T]: R };

  for (const [key, value] of Object.entries(obj) as [keyof T, T[keyof T]][]) {
    result[key] = fn(value, key, obj);
  }

  return result;
}

export const mapObjectFieldsKey = <
  T extends Record<string, unknown>,
  MapResult,
  Result extends Record<keyof T, MapResult>,
>(
  obj: T,
  mapFn: (value: T[keyof T], key: $Keys<T>) => MapResult,
): Result => {
  return Object.keys(obj).reduce((prev, next) => {
    const key = next as $Keys<T>;
    // there is no way to satisfy typescript because key of T is "string"
    // and no way to make sure it is the same keys on type level
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    prev[key] = mapFn(obj[key], key) as any;
    return prev;
  }, {} as Result);
};

export const toUndefined = () => undefined;

interface OverwriteObjectOptions {
  overwriteWithNull?: boolean;
  recursive?: boolean;
}

/**
 * Function to overwrite object fields with new value
 * @description
 * - overwrite object fields with new value
 * - if new value is null, it will not overwrite the field (unless overwriteWithNull is true)
 * - if field is object, it will recursively overwrite the object fields (unless recursive is false)
 * - if field is array, it will overwrite the array with new value
 * @param obj - object to be overwritten
 * @param fields - new fields to overwrite
 * @param options - overwrite options
 * @param options.recursive - recursively overwrite object fields (arrays are not considered as object)
 * @default true
 * @param options.overwriteWithNull - null value is considered as valid value to overwrite
 * @default false
 * @example
 * const obj = { a: 1, b: 2, c: { d: 3, e: 4 }, f: [5, "6"] }
 * const fields = { a: 5, c: { d: 6 }, f: [7] }
 * overwriteObjectFields(obj, fields)
 * // Returns { a: 5, b: 2, c: { d: 6, e: 4 }, f: [7] }
 * @returns
 */
export const overwriteObjectFields = <T extends Record<string, unknown>>(
  obj: T,
  fields: RecursivePartial<T>,
  { overwriteWithNull = false, recursive = true }: OverwriteObjectOptions = {},
): T => {
  return Object.keys(obj).reduce((prev, next) => {
    const key = next as $Keys<T>;
    if (
      recursive &&
      !Array.isArray(obj[key]) &&
      typeof obj[key] === "object" &&
      typeof fields[key] === "object"
    ) {
      prev[key] = overwriteObjectFields(
        obj[key] as Record<string, unknown>,
        fields[key] as Record<string, unknown>,
      ) as T[$Keys<T>];
    } else if (overwriteWithNull) {
      prev[key] = (
        typeof fields[key] !== "undefined" ? fields[key] : obj[key]
      ) as T[$Keys<T>];
    } else {
      prev[key] = (fields[key] ?? obj[key]) as T[$Keys<T>];
    }
    return prev;
  }, {} as T);
};

/**
 * Deeply traverses and transforms an object or array, returning a new structure.
 *
 * @param obj - The object or array to traverse.
 * @param callback - A function that transforms each key/value.
 *   - `key`: the property key
 *   - `value`: the property value
 *   - `path`: an array of keys representing the path from the root
 *   - Should return the new value to assign at this position
 * @param path - (Internal) The current path of keys. Used in recursion.
 *
 * @returns A new object or array with all values transformed by the callback.
 *
 * @example
 * const data = {
 *   user: { id: 1, name: "Alice", address: { city: "Wonderland" } },
 *   items: [{ id: "a", price: 10 }, { id: "b", price: 20 }]
 * };
 *
 * const uppercased = deepMap(data, (key, value, path) => {
 *   if (typeof value === "string") return value.toUpperCase();
 *   return value;
 * });
 *
 * console.log(uppercased);
 * // {
 * //   user: { id: 1, name: "ALICE", address: { city: "WONDERLAND" } },
 * //   items: [{ id: "a", price: 10 }, { id: "b", price: 20 }]
 * // }
 */
export function deepMap(
  obj: unknown,
  callback: (key: string, value: unknown, path: string[]) => unknown,
  path: string[] = [],
): unknown {
  if (Array.isArray(obj)) {
    return obj.map((value, index) =>
      deepMap(value, callback, [...path, String(index)]),
    );
  } else if (obj !== null && typeof obj === "object") {
    const result: Record<string, unknown> = {};
    for (const key in obj as Record<string, unknown>) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = (obj as Record<string, unknown>)[key];
        const currentPath = [...path, key];
        // Apply transformation
        const transformed = callback(key, value, currentPath);
        // Recurse if object/array
        result[key] =
          transformed !== null && typeof transformed === "object"
            ? deepMap(transformed, callback, currentPath)
            : transformed;
      }
    }
    return result;
  }
  return obj;
}

export function isEmptyObject(obj: unknown) {
  return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
}

export function isDeepEqual(obj1: unknown, obj2: unknown) {
  if (obj1 === obj2) return true;

  if (
    typeof obj1 !== "object" ||
    obj1 === null ||
    typeof obj2 !== "object" ||
    obj2 === null
  ) {
    return false;
  }

  const keys1 = Object.keys(obj1) as (keyof typeof obj1)[];
  const keys2 = Object.keys(obj2) as (keyof typeof obj2)[];

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (!keys2.includes(key) || !isDeepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
}
